import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addToWishList, { AddToWishListParameter } from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps, StyledIcon } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomHeartIcon from "@tSurya/Icons/CustomHeartIcon";
import GVHeartIcon from "@tSurya/Icons/GVHeartIcon";
import { buttonStyles } from "@tSurya/StylesExtensions/IconStyle";
import { ELEMENTS, TYPOGRAPHY } from "@tSurya/theme/design";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { isGlobalViewsWebsite, isMgbwWebsite, usesGvStylePdpLayout } from "@tSurya/Common/Utilities/websiteUtils";
import { getConfiguratorSelections, getShouldUseSelectionImage, getUnsupportedCoverCylindoFeatures } from "@tSurya/Store/Data/ConfiguratorSelectors";
import CustomApplicationState from "@tSurya/Store/Data/CustomApplicationState/CustomApplicationState";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { MGBW_CYLINDO_FEATURES, MGBW_CYLINDO_UNSUPPORTED_INPUT, MGBW_SELECTIONS_INPUT } from "@tSurya/Common/MgbwPropertyKeys";
interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ProductAddToListLinkStyles;
    showIcon?: boolean;
    fromPDP?: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasProductContext;

const mapStateToProps = (state: CustomApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    location: getLocation(state),
    configuratorSelections: getConfiguratorSelections(state),
    validatedCylindoFeatures: state.SURData.configuratorStore.validatedCylindoFeatures,
    shouldUseSelectionImage: getShouldUseSelectionImage(state),
    unsupportedCoverCylindoFeatures: getUnsupportedCoverCylindoFeatures(state),
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

export interface ProductAddToListLinkStyles {
    link?: InjectableCss;
    wrapper?: InjectableCss;
    heartIcon?: InjectableCss;
    button?: InjectableCss;
    heartIconStyles?: IconPresentationProps;
    innerWrapper?: InjectableCss;
    modal?: ModalPresentationProps;
    SizeMessageText?: TypographyProps;
}

const linkStyling = () => {
    if (usesGvStylePdpLayout()) {
        // MGBW PDP matches the Figma "Add to Project" / "Share Design" link style
        // (Manrope Medium 500, 15px, line-height 1.5, letter-spacing 0.08px, #5e7a80).
        // On MGBW the link sits side by side with Share Design in a FlexRow (Figma 3407:24180),
        // so it must be inline with no outer margin; GV keeps its stacked full-width layout.
        const isMgbw = isMgbwWebsite();
        return css`
            outline: none !important;
            width: ${isMgbw ? "auto" : ELEMENTS.productDetails.addToProjectButton.width};
            display: ${isMgbw ? "inline-flex" : "block"};
            ${isMgbw ? "align-items: center; justify-content: center;" : ""}
            margin-top: ${isMgbw ? "0" : "20px"};
            padding: 0;
            text-align: center;
            height: 40px;
            ${isMgbw ? "svg path { stroke: #5e7a80; }" : ""}
            span {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${isMgbw ? 500 : TYPOGRAPHY.weight.mediumbold};
                font-size: ${TYPOGRAPHY.size.base};
                line-height: ${isMgbw ? "1.5" : TYPOGRAPHY.lineHeight.xbase};
                ${isMgbw ? "letter-spacing: 0.08px;" : ""}
                text-align: center;
                color: ${isMgbw ? "#5e7a80" : getColor("text.link")}!important;
            }
        `;
    }
    return css`
        display: inline-flex;
        height: ${ELEMENTS.productDetails.addToCartButton.height};
        padding: 0px 20.5px;
        border: 2px solid rgb(50, 49, 50);
        outline-color: transparent !important;
        white-space: nowrap;

        span {
            font-size: 15px;
        }
    `;
};

export const productAddToListLinkStyles: ProductAddToListLinkStyles = {
    wrapper: {
        css: css`
            text-align: center;
            display: flex;
            background: ${getColor("common.background")};
            border: 2px solid rgb(50, 49, 50);
            padding: 10px 40px;
            display: flex;
            justify-content: center;
            cursor: pointer;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 0px;
                    `,

                    css`
                        margin-left: 0px;
                    `,

                    css`
                        margin-left: 0px;
                    `,

                    null,

                    null,
                ])}
        `,
    },
    button: {
        css: css`
            margin-left: 15px;
            width: ${ELEMENTS.productDetails.addToProjectButton.width};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 5px;
                    `,

                    css`
                        margin-left: 5px;
                    `,

                    css`
                        margin-left: 10px;
                    `,
                    css`
                        margin-left: 15px;
                    `,
                    css`
                        margin-left: 15px;
                    `,
                ])}
            white-space: nowrap;
        `,
    },
    link: {
        css: linkStyling(),
    },
    heartIcon: {
        css: css`
            display: flex;
        `,
    },
    heartIconStyles: {
        size: 16,
    },
    modal: {
        size: 350,
        cssOverrides: {
            modalBody: css`
                overflow-y: visible;
            `,
            modalTitle: css`
                padding: 10px 20px;
            `,
            modalContent: css`
                padding: 20px;
            `,
        },
    },
};

/**
 * @deprecated Use productAddToListLinkStyles instead.
 */
export const productAddToListLink = productAddToListLinkStyles;

const ProductAddToListLink: React.FC<Props> = ({
    productContext: { product, productInfo },
    wishListSettings,
    labelOverride,
    setAddToListModalIsOpen,
    addToWishList,
    extendedStyles,
    fromPDP = false,
    showIcon = false,
    variantSelectionCompleted,
    signInUrl,
    location,
    configuratorSelections,
    validatedCylindoFeatures,
    shouldUseSelectionImage,
    unsupportedCoverCylindoFeatures,
    ...otherProps
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const [styles] = React.useState(() => mergeToNew(productAddToListLink, extendedStyles));
    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    const addToProjectIconStyle = React.useMemo<React.CSSProperties | undefined>(() => (usesGvStylePdpLayout() ? { marginTop: "-2px" } : undefined), []);

    if (product.id.includes("Temp")) {
        return null;
    }
    const addToListLinkClickHandler = () => {
        const hasConfiguratorSelections = !!configuratorSelections && Object.keys(configuratorSelections).length > 0;
        const productInfoWithProperties: AddToWishListParameter["productInfos"][number] & {
            properties?: Record<string, string>;
        } = {
            ...productInfo,
            properties: hasConfiguratorSelections
                ? {
                      [MGBW_SELECTIONS_INPUT]: JSON.stringify(configuratorSelections),
                      ...(validatedCylindoFeatures && Object.keys(validatedCylindoFeatures).length > 0
                          ? { [MGBW_CYLINDO_FEATURES]: JSON.stringify(validatedCylindoFeatures) }
                          : {}),
                      ...(shouldUseSelectionImage
                          ? { [MGBW_CYLINDO_UNSUPPORTED_INPUT]: JSON.stringify(unsupportedCoverCylindoFeatures) }
                          : {}),
                  }
                : undefined,
        };

        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfoWithProperties],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.(resultProps.result?.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result?.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }
        if (fromPDP) {
            if (variantSelectionCompleted || (configuratorSelections && Object.keys(configuratorSelections).length > 0)) {
                setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfoWithProperties] });
            } else {
                setModalIsOpen(true);
            }
        } else {
            setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfoWithProperties] });
        }
    };
    const customStyleButton = buttonStyles;
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const returnUrl = location.pathname + location.search;
    return (
        <>
            {!showIcon ? (
                // <StyledWrapper onClick={addToListLinkClickHandler} {...styles.wrapper}>
                //     <StyledWrapper {...styles.heartIcon}>
                //         <Icon src={CustomHeartIcon}></Icon>
                //     </StyledWrapper>
                //     <StyledWrapper {...styles.button}>
                //         <Link {...styles.link} disabled={productInfo.qtyOrdered <= 0} {...otherProps}>
                //             {labelOverride ?? translate("Add to Project")}
                //         </Link>
                //     </StyledWrapper>
                // </StyledWrapper>
                <Link
                    aria-label="Add to Project"
                    {...customStyleButton.iconSecondryLightBackground}
                    {...styles.link}
                    disabled={productInfo.qtyOrdered <= 0}
                    onClick={addToListLinkClickHandler}
                >
                    <StyledIcon
                        src={isGlobalViewsWebsite() ? GVHeartIcon : CustomHeartIcon}
                        size={isMgbwWebsite() ? 16 : ELEMENTS.productDetails.addToProjectButton.iconSize}
                        style={addToProjectIconStyle}
                    />
                    {labelOverride ?? translate("Add to Project")}
                </Link>
            ) : (
                <Icon src={isGlobalViewsWebsite() ? GVHeartIcon : CustomHeartIcon} onClick={addToListLinkClickHandler} {...styles.heartIconStyles}></Icon>
            )}
            {modalIsOpen && (
                <Modal {...styles.modal} headline={translate("Select Size")} isOpen={modalIsOpen} handleClose={modalCloseHandler}>
                    <StyledWrapper {...styles.innerWrapper} data-test-selector="productAddToListModal">
                        <Typography data-test-selector="productAddToListModal_requireSignIn" {...styles.SizeMessageText}>
                            {siteMessage("List_Must_Select_Size", `${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`)}
                        </Typography>
                    </StyledWrapper>
                </Modal>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductAddToListLink));
